import React, { useState } from 'react';
import { ArrowRight, Clock, User, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';

const Resources = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');

  const featuredPost = {
    title: 'Hire a Pitch Deck Professional or Make Your Own? What\'s Best for Your Startup',
    excerpt: 'A comprehensive breakdown of when DIY pitch decks work, when to hire a professional, and the hidden costs of getting it wrong.',
    author: 'Arshdeep Singh',
    date: 'May 25, 2026',
    readTime: '8 min read',
    category: 'Pitch Decks',
    image: 'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=800&h=400&fit=crop'
  };

  const blogPosts = [
    { title: 'Startup Fundraising Strategies Every Founder Should Know', excerpt: 'Beyond the basics: advanced fundraising strategies that separate funded founders from the rest.', author: 'Mohd Sajid', readTime: '6 min read', category: 'Strategy', image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=600&h=300&fit=crop' },
    { title: 'How to Build Investor-Ready Financial Models', excerpt: 'The 5 financial model mistakes that kill fundraising deals and how to avoid them.', author: 'Arshdeep Singh', readTime: '7 min read', category: 'Finance', image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=300&fit=crop' },
    { title: 'Pre-Seed vs Seed vs Series A: Understanding Your Funding Stage', excerpt: 'Confused about funding stages? This guide breaks down what each stage means and what investors expect.', author: 'Mohd Sajid', readTime: '5 min read', category: 'Fundamentals', image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=600&h=300&fit=crop' },
    { title: 'The Art of the Cold Email to Investors', excerpt: 'Templates, frameworks, and real examples of cold emails that actually got investor meetings.', author: 'Arshdeep Singh', readTime: '6 min read', category: 'Outreach', image: 'https://images.unsplash.com/photo-1596524430615-b46475ddff6e?w=600&h=300&fit=crop' },
    { title: 'How to Negotiate Term Sheets Without Lawyers (At First)', excerpt: 'Understanding the key terms that matter most and how to negotiate them before getting legal involved.', author: 'Mohd Sajid', readTime: '9 min read', category: 'Legal', image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&h=300&fit=crop' },
    { title: 'Building Your Data Room: A Step-by-Step Guide', excerpt: 'Everything that goes into an investor-ready data room and how to organize it for maximum efficiency.', author: 'Arshdeep Singh', readTime: '7 min read', category: 'Documentation', image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=300&fit=crop' }
  ];

  const categories = ['All', 'Strategy', 'Pitch Decks', 'Finance', 'Outreach', 'Legal', 'Documentation', 'Fundamentals'];
  const articleSlugs = [
  'hire-pitch-deck-professional',
  'startup-fundraising-strategies',
  'investor-ready-financial-models',
  'pre-seed-seed-series-a',
  'cold-email-to-investors',
  'negotiate-term-sheets',
  'building-your-data-room'
];
  const filteredBlogPosts = selectedCategory === 'All'
  ? blogPosts
  : blogPosts.filter((post) => post.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <section className="relative py-24 bg-gradient-to-br from-emerald-600 via-green-600 to-emerald-700 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Resources & <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-200 to-emerald-100">Insights</span>
            </h1>
            <p className="text-xl text-green-50 leading-relaxed">
              Real fundraising advice from people who've been in the trenches. No fluff, just practical insights.
            </p>
          </div>
        </div>
      </section>

      <section className="py-8 border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3 max-w-5xl mx-auto">
            {categories.map((cat, index) => (
              <button key={index} 
                onClick={() => setSelectedCategory(cat)}
                data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                  index === 0
                    ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-lg'
                    : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-emerald-300 hover:text-emerald-600'
                }`}>
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-2 mb-6">
              <div className="h-1 w-12 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full"></div>
              <span className="text-emerald-600 font-bold text-sm uppercase tracking-wider">Featured Article</span>
            </div>

            <Card
  onClick={() => navigate(`/resources/${articleSlugs[0]}`)}
  className="border-2 border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-500 group cursor-pointer"
  data-testid="featured-post"
>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="h-80 lg:h-auto overflow-hidden">
                  <img src={featuredPost.image} alt={featuredPost.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <CardContent className="p-10 flex flex-col justify-center">
                  <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold uppercase tracking-wider mb-4 w-fit">
                    {featuredPost.category}
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 group-hover:text-emerald-600 transition-colors duration-300">
                    {featuredPost.title}
                  </h2>
                  <p className="text-gray-600 text-lg leading-relaxed mb-6">{featuredPost.excerpt}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
                    <div className="flex items-center gap-2"><User className="h-4 w-4" /><span>{featuredPost.author}</span></div>
                    <div className="flex items-center gap-2"><Clock className="h-4 w-4" /><span>{featuredPost.readTime}</span></div>
                  </div>
                  <Button className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white w-fit">
                    Read Full Article<ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </div>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-slate-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Latest Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogPosts.map((post, index) => (
               <Card
  key={index}
  onClick={() => navigate(`/resources/${articleSlugs[blogPosts.indexOf(post) + 1]}`)}
  className="border-2 border-gray-100 hover:border-emerald-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group cursor-pointer overflow-hidden"
  data-testid={`blog-post-${index}`}
>
                  <div className="h-48 overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  <CardContent className="p-6">
                    <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold uppercase tracking-wider mb-3">
                      {post.category}
                    </span>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-emerald-600 transition-colors duration-300 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-sm text-gray-500"><User className="h-4 w-4" /><span>{post.author}</span></div>
                      <div className="flex items-center gap-2 text-sm text-gray-500"><Clock className="h-4 w-4" /><span>{post.readTime}</span></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-emerald-600 to-green-600">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <BookOpen className="h-16 w-16 text-white mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Get Fundraising Insights Weekly</h2>
            <p className="text-xl text-green-50 mb-8">Join 1,000+ founders getting actionable fundraising advice in their inbox</p>
            <form
  action="https://formsubmit.co/e319b3353985b93c53fab49021e163d3"
  method="POST"
  className="flex flex-col sm:flex-row gap-3 justify-center"
>
  <input
    type="email"
    name="email"
    placeholder="Enter your email address"
    required
    className="px-6 py-4 rounded-lg text-gray-900 w-full sm:w-80 focus:outline-none focus:ring-2 focus:ring-white"
  />

  <input type="hidden" name="_subject" value="New FounderScale Newsletter Subscription" />
  <input type="hidden" name="_template" value="table" />
  <input type="hidden" name="source" value="Resources Newsletter" />
  <input type="hidden" name="_next" value="https://founderscalecapital.com/resources" />

  <Button
    type="submit"
    size="lg"
    data-testid="resources-cta-btn"
    className="bg-white text-emerald-700 hover:bg-green-50 text-lg px-8 py-6 shadow-2xl font-bold"
  >
    Subscribe Now
    <ArrowRight className="ml-2 h-5 w-5" />
  </Button>
</form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
